import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn, MessageSquare, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import usePageVisibility from '../hooks/usePageVisibility';
import SuggestionsModal from './SuggestionsModal';
import CountyContactModal from './CountyContactModal';

export default function Navbar({ showNav = true }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showCountyContact, setShowCountyContact] = useState(false);
  const location = useLocation();
  const { visibility } = usePageVisibility();
  
  // All nav links with visibility keys
  const allNavLinks = [
    { name: t('nav.home'), path: '/', key: 'home' },
    { name: t('nav.about'), path: '/about', key: 'about' },
    { name: 'Our Mission', path: '/mission', key: 'mission' },
    { name: 'Impact Stories', path: '/impact', key: 'impact' },
    { name: t('nav.sermons'), path: '/sermons', key: 'sermons' },
    { name: t('nav.podcast'), path: '/podcast', key: 'podcast' },
    { name: t('nav.watch'), path: '/watch', key: 'watch' },
    { name: t('nav.events'), path: '/events', key: 'events' },
    { name: t('nav.programs'), path: '/programs', key: 'programs' },
    { name: 'Outreach Mission', path: '/outreach-mission', key: 'outreachMission' },
    { name: 'Animal Rescue', path: '/animal-rescue', key: 'animalRescue' },
    { name: 'Resources', path: '/resources', key: 'resources' },
    { name: 'Local Businesses', path: '/local-businesses', key: 'localBusinesses' },
    { name: t('nav.blog'), path: '/blog', key: 'blog' },
    { name: t('nav.prayers'), path: '/prayers', key: 'prayers' },
    { name: t('nav.qa'), path: '/qa', key: 'qa' },
    { name: t('nav.partners'), path: '/partners', key: 'partners' },
    { name: t('nav.volunteer'), path: '/volunteer', key: 'volunteer' },
    { name: t('nav.contact'), path: '/contact', key: 'contact' },
  ];
  
  // Filter to only visible pages
  const navLinks = allNavLinks.filter(link => visibility[link.key] !== false);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);
  
  const isActive = (path) => location.pathname === path;
  
  if (!showNav) return null;
  
  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className={`fixed top-4 left-4 z-50 transition-all duration-500 ${
          scrolled 
            ? 'bg-white/95 backdrop-blur-lg shadow-lg' 
            : 'bg-white/80 backdrop-blur-md'
        } border border-gold/20 rounded-full px-4 md:px-6 py-3`}
        data-testid="main-navbar"
      >
        <div className="flex items-center gap-3 md:gap-5">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_d52ae5dd-568a-4669-9c8f-951ff5c53e99/artifacts/g3qmizx9_IMG_2894.png" 
              alt="Living Fruit Ministry" 
              className="h-10 w-auto"
            />
          </Link>
          
          {/* Hamburger Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-charcoal hover:text-burgundy transition-colors"
            data-testid="menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          {/* Language Switcher - On the right for dropdown space */}
          <LanguageSwitcher className={scrolled ? 'text-charcoal' : 'text-charcoal'} />
        </div>
      </motion.nav>
      
      {/* Full Menu - Same for all screen sizes */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
              onClick={() => setIsOpen(false)}
              data-testid="menu-backdrop"
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed top-20 left-4 right-4 md:left-auto md:right-8 md:w-80 z-50 bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl border border-gold/20 p-6 max-h-[80vh] overflow-y-auto"
              data-testid="nav-menu"
            >
              <div className="grid grid-cols-2 gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                    className={`text-sm font-medium py-3 px-4 rounded-xl transition-all duration-200 ${
                      isActive(link.path) 
                        ? 'bg-burgundy text-white' 
                        : 'text-charcoal hover:bg-gold/10'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
              
              {/* Quick Actions */}
              <div className="mt-6 pt-4 border-t border-gold/10">
                <div className="flex gap-2">
                  <Link
                    to="/donate"
                    onClick={() => setIsOpen(false)}
                    className="flex-1 text-center text-sm font-medium py-3 px-4 bg-burgundy text-white rounded-xl hover:bg-purple transition-colors"
                    data-testid="nav-quick-donate"
                  >
                    {t('nav.donate')}
                  </Link>
                  <Link
                    to="/im-new"
                    onClick={() => setIsOpen(false)}
                    className="flex-1 text-center text-sm font-medium py-3 px-4 bg-gold text-charcoal rounded-xl hover:bg-gold/80 transition-colors"
                    data-testid="nav-quick-new"
                  >
                    {t('nav.imNew')}
                  </Link>
                </div>
              </div>

              {/* Staff Sign In */}
              <div className="mt-4 pt-4 border-t border-gold/10">
                <Link
                  to="/admin/login"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center gap-2 w-full text-sm font-medium py-3 px-4 bg-charcoal/5 text-charcoal/70 rounded-xl hover:bg-charcoal/10 hover:text-charcoal transition-colors"
                  data-testid="nav-staff-signin"
                >
                  <LogIn size={16} />
                  Staff Sign In
                </Link>
              </div>

              {/* County Contact */}
              <div className="mt-3">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setShowCountyContact(true);
                  }}
                  className="flex items-center justify-center gap-2 w-full text-sm font-medium py-3 px-4 bg-amber-100 text-amber-800 rounded-xl hover:bg-amber-200 transition-colors"
                  data-testid="nav-county-contact-btn"
                >
                  <MapPin size={16} />
                  Contact Your County Director
                </button>
              </div>

              {/* Suggestions / Feedback */}
              <div className="mt-3">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setShowSuggestions(true);
                  }}
                  className="flex items-center justify-center gap-2 w-full text-sm font-medium py-3 px-4 bg-gold/10 text-charcoal rounded-xl hover:bg-gold/20 transition-colors"
                  data-testid="nav-suggestions-btn"
                >
                  <MessageSquare size={16} />
                  Suggestions & Feedback
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Suggestions Modal */}
      <SuggestionsModal 
        isOpen={showSuggestions} 
        onClose={() => setShowSuggestions(false)} 
      />

      {/* County Contact Modal */}
      <CountyContactModal
        isOpen={showCountyContact}
        onClose={() => setShowCountyContact(false)}
      />
    </>
  );
}
